import { Link } from 'react-router-dom';

import { AuthError } from '@/modules/auth/components/AuthError';
import { AuthLayout } from '@/modules/layouts/components/authLayout/AuthLayout';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { SlackLogo } from '@/modules/signup-flow/components/slackIntegration/SlackLogo';
import { Button } from '@/modules/ui/components/button/Button';
import { ProviderMark } from '@/pages/login/providerMark/ProviderMark';

export interface SlackCallbackError {
  message: string;
  /** Sign-up link that keeps a still-valid trial offer code. */
  signUpPath: string;
}

interface SlackCallbackStatusProps {
  /** Absent while the Slack sign-in is still being exchanged. */
  error?: SlackCallbackError;
}

const logo = <SlackLogo className="size-7" />;

/** What `/auth/callback/slack` shows while signing in or after it fails. */
export const SlackCallbackStatus = ({ error }: SlackCallbackStatusProps) => {
  if (!error) {
    return (
      <>
        <DocumentMetadata title="Signing you in" />
        <AuthLayout
          mark={<ProviderMark provider="Slack" logo={logo} />}
          heading="Signing you in"
          subheading="Finishing your Slack sign-in. This usually takes a few seconds."
        />
      </>
    );
  }

  return (
    <AuthLayout
      mark={<ProviderMark provider="Slack" logo={logo} failed />}
      heading="Slack sign-in failed"
      subheading="We couldn’t sign you in with Slack. Go back and try again, or use another sign-in method."
      switchLink={{
        prompt: 'New to Finlay?',
        action: 'Create an account',
        to: error.signUpPath,
      }}
    >
      <DocumentMetadata title="Slack sign-in failed" />
      <AuthError message={error.message} />
      <Button
        variant="primary"
        className="h-9 w-full rounded-md active:scale-[0.98]"
        asChild
      >
        <Link to="/login">Back to sign in</Link>
      </Button>
    </AuthLayout>
  );
};
